// src/pages/finance-portal/inventory/Locations.js
import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent,
    DialogTitle, FormControl, FormControlLabel, Grid, IconButton,
    InputLabel, MenuItem, Paper, Select, Stack, Switch,
    Table, TableBody, TableCell, TableHead, TableRow, TextField,
    Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import StarIcon from '@mui/icons-material/Star';
import axiosInstance from '../../../utils/axiosInstance';
import { FONT_FAMILY, INV_ACCENT, INV_ACCENT_DARK } from './components/inventoryFormatters';

const TYPES = ['WAREHOUSE', 'STORE', 'VAN', 'DROPSHIP', 'QUARANTINE', 'VIRTUAL'];
const STATUSES = ['ACTIVE', 'INACTIVE', 'ARCHIVED'];

const EMPTY_LOC = {
    code: '', name: '', location_type: 'WAREHOUSE', status: 'ACTIVE',
    address_line1: '', address_line2: '', city: '', state: '',
    postal_code: '', country: '',
    contact_name: '', contact_email: '', contact_phone: '',
    is_default: false, is_sellable_from: true, is_receivable_to: true,
    timezone: 'UTC', notes: '',
};

export default function Locations() {
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dialog, setDialog] = useState({ open: false, mode: 'create', data: EMPTY_LOC });
    const [deleteDialog, setDeleteDialog] = useState({ open: false, location: null });
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});

    const fetchLocations = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/api/finance/inventory/locations');
            if (res.data?.success) setLocations(res.data.data || []);
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to load locations');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchLocations(); }, [fetchLocations]);

    const openNew = () => setDialog({ open: true, mode: 'create', data: { ...EMPTY_LOC } });
    const openEdit = (loc) => setDialog({ open: true, mode: 'edit', data: { ...loc } });

    const set = (k, v) => setDialog((d) => ({ ...d, data: { ...d.data, [k]: v } }));

    const handleSave = async () => {
        setSaving(true);
        setErrors({});
        try {
            if (dialog.mode === 'create') {
                await axiosInstance.post('/api/finance/inventory/locations', dialog.data);
            } else {
                await axiosInstance.put(`/api/finance/inventory/locations/${dialog.data.id}`, dialog.data);
            }
            setDialog({ open: false, mode: 'create', data: EMPTY_LOC });
            fetchLocations();
        } catch (e) {
            const serverErrors = e.response?.data?.errors;
            const msg = e.response?.data?.message || 'Save failed';
            if (serverErrors && typeof serverErrors === 'object') {
                setErrors(serverErrors);
                const firstField = Object.keys(serverErrors)[0];
                alert(`${msg}: ${serverErrors[firstField]}`);
            } else {
                alert(msg);
            }
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        try {
            await axiosInstance.delete(`/api/finance/inventory/locations/${deleteDialog.location.id}`);
            setDeleteDialog({ open: false, location: null });
            fetchLocations();
        } catch (e) {
            alert(e.response?.data?.message || 'Delete failed');
        }
    };

    return (
        <Box sx={{ p: 3, fontFamily: FONT_FAMILY, bgcolor: '#f8fafc', minHeight: '100vh' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box>
                    <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '1.65rem', fontWeight: 700, color: '#0f172a' }}>
                        Locations
                    </Typography>
                    <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.9rem', color: '#64748b', mt: 0.5 }}>
                        Warehouses, stores, and partner locations where stock is held
                    </Typography>
                </Box>
                <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}
                    sx={{ fontFamily: FONT_FAMILY, textTransform: 'none',
                          bgcolor: INV_ACCENT, '&:hover': { bgcolor: INV_ACCENT_DARK } }}>
                    Add Location
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Code</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Name</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Type</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Address</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Status</TableCell>
                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={6} sx={{ p: 3, textAlign: 'center' }}>Loading…</TableCell></TableRow>
                        ) : locations.length === 0 ? (
                            <TableRow><TableCell colSpan={6} sx={{ p: 6, textAlign: 'center', color: '#64748b' }}>
                                No locations yet. Add one to start tracking stock.
                            </TableCell></TableRow>
                        ) : (
                            locations.map((l) => (
                                <TableRow key={l.id} hover>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            {l.is_default && <Tooltip title="Default location"><StarIcon fontSize="small" sx={{ color: '#f59e0b' }} /></Tooltip>}
                                            <span>{l.code}</span>
                                        </Stack>
                                    </TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY }}>{l.name}</TableCell>
                                    <TableCell>
                                        <Chip size="small" label={l.location_type} sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} />
                                    </TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.82rem', color: '#64748b' }}>
                                        {[l.address_line1, l.city, l.state, l.country].filter(Boolean).join(', ') || '—'}
                                    </TableCell>
                                    <TableCell>
                                        <Chip size="small" label={l.status}
                                            sx={{
                                                fontFamily: FONT_FAMILY, fontWeight: 600,
                                                bgcolor: l.status === 'ACTIVE' ? '#dcfce7' : '#e5e7eb',
                                                color: l.status === 'ACTIVE' ? '#166534' : '#374151',
                                            }} />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(l)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                                        <Tooltip title="Delete"><IconButton size="small" onClick={() => setDeleteDialog({ open: true, location: l })}>
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton></Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </Paper>

            {/* Location create/edit dialog */}
            <Dialog open={dialog.open} onClose={() => setDialog({ ...dialog, open: false })} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontFamily: FONT_FAMILY }}>
                    {dialog.mode === 'create' ? 'New Location' : `Edit ${dialog.data.code}`}
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                            <TextField size="small" fullWidth label="Code"
                                value={dialog.data.code || ''} onChange={(e) => set('code', e.target.value)} />
                        </Grid>
                        <Grid item xs={12} sm={8}>
                            <TextField size="small" fullWidth label="Name" required
                                value={dialog.data.name || ''} onChange={(e) => set('name', e.target.value)} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Type</InputLabel>
                                <Select value={dialog.data.location_type} label="Type"
                                    onChange={(e) => set('location_type', e.target.value)}>
                                    {TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select value={dialog.data.status} label="Status"
                                    onChange={(e) => set('status', e.target.value)}>
                                    {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField size="small" fullWidth label="Address Line 1"
                                value={dialog.data.address_line1 || ''} onChange={(e) => set('address_line1', e.target.value)} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField size="small" fullWidth label="Address Line 2"
                                value={dialog.data.address_line2 || ''} onChange={(e) => set('address_line2', e.target.value)} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField size="small" fullWidth label="City"
                                value={dialog.data.city || ''} onChange={(e) => set('city', e.target.value)} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField size="small" fullWidth label="State"
                                value={dialog.data.state || ''} onChange={(e) => set('state', e.target.value)} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField size="small" fullWidth label="Postal Code"
                                value={dialog.data.postal_code || ''} onChange={(e) => set('postal_code', e.target.value)} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField size="small" fullWidth label="Country"
                                value={dialog.data.country || ''} onChange={(e) => set('country', e.target.value)} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField size="small" fullWidth label="Contact Name"
                                value={dialog.data.contact_name || ''} onChange={(e) => set('contact_name', e.target.value)} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField size="small" fullWidth label="Contact Email"
                                value={dialog.data.contact_email || ''} onChange={(e) => set('contact_email', e.target.value)} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField size="small" fullWidth label="Contact Phone"
                                value={dialog.data.contact_phone || ''} onChange={(e) => set('contact_phone', e.target.value)} />
                        </Grid>
                        <Grid item xs={12}>
                            <Stack direction="row" spacing={3}>
                                <FormControlLabel control={<Switch checked={!!dialog.data.is_default}
                                    onChange={(e) => set('is_default', e.target.checked)} />} label="Default" />
                                <FormControlLabel control={<Switch checked={!!dialog.data.is_sellable_from}
                                    onChange={(e) => set('is_sellable_from', e.target.checked)} />} label="Sellable from" />
                                <FormControlLabel control={<Switch checked={!!dialog.data.is_receivable_to}
                                    onChange={(e) => set('is_receivable_to', e.target.checked)} />} label="Receivable to" />
                            </Stack>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField size="small" fullWidth multiline rows={2} label="Notes"
                                value={dialog.data.notes || ''} onChange={(e) => set('notes', e.target.value)} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialog({ ...dialog, open: false })} sx={{ fontFamily: FONT_FAMILY, textTransform: 'none' }}>Cancel</Button>
                    <Button variant="contained" disabled={saving} onClick={handleSave}
                        sx={{ fontFamily: FONT_FAMILY, textTransform: 'none', bgcolor: INV_ACCENT, '&:hover': { bgcolor: INV_ACCENT_DARK } }}>
                        {saving ? 'Saving…' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, location: null })}>
                <DialogTitle sx={{ fontFamily: FONT_FAMILY }}>Delete location?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily: FONT_FAMILY }}>
                        Delete <b>{deleteDialog.location?.name}</b>?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialog({ open: false, location: null })}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
